import React, { useCallback, useEffect, useState } from 'react';
import { View, Alert, Platform, Text, useWindowDimensions } from 'react-native';
import { Button, ActivityIndicator, Portal, Modal } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import EmptyState from '../components/EmptyState';
import EditCredentialsModal from '../components/EditCredentialsModal';
import {
  Avatar, Badge, Card, FilterChip, PageHeader, Screen, SectionHeader, TextInput, toneColors,
} from '../components/ui';
import type { BadgeTone } from '../components/ui';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { userService, AppUser } from '../services/userService';
import { isAdminOrSuperAgentRole, isSuperAdminRole, logout } from '../redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { roleLabel } from '../navigation/navItems';

function confirmAction(message: string, onConfirm: () => void) {
  if (Platform.OS === 'web') {
    const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
    const confirmed = typeof webGlobal.confirm === 'function' ? webGlobal.confirm(message) : true;
    if (confirmed) onConfirm();
    return;
  }
  Alert.alert('Confirmer', message, [
    { text: 'Annuler', style: 'cancel' },
    { text: 'Envoyer', onPress: onConfirm },
  ]);
}

const roleTone = (role: string): BadgeTone => {
  if (role === 'ROLE_SUPER_ADMIN') return 'danger';
  if (role === 'ROLE_ADMIN') return 'primary';
  if (role === 'ROLE_SUPER_AGENT') return 'brand';
  return 'neutral';
};

const ROLE_OPTIONS: { value: 'ROLE_AGENT' | 'ROLE_SUPER_AGENT'; label: string }[] = [
  { value: 'ROLE_AGENT', label: 'Agent' },
  { value: 'ROLE_SUPER_AGENT', label: 'Super agent' },
];

/** Inline success / error message. */
function Banner({ tone, message }: { tone: 'success' | 'danger'; message: string }) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { fg, bg } = toneColors(colors, tone);
  return (
    <View style={[styles.banner, { backgroundColor: bg }]} accessibilityRole="alert">
      <MaterialCommunityIcons
        name={tone === 'success' ? 'check-circle-outline' : 'alert-circle-outline'}
        size={20}
        color={fg}
      />
      <Text style={[styles.bannerText, { color: fg }]}>{message}</Text>
    </View>
  );
}

type UserRowProps = {
  user: AppUser;
  first: boolean;
  resettingId: number | null;
  onReset: () => void;
  /** Super administrator only. */
  onEditCredentials?: () => void;
};

function UserRow({ user, first, resettingId, onReset, onEditCredentials }: UserRowProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const displayName = user.nom || user.username;

  return (
    <View style={[styles.userRow, !first && styles.rowDivider]}>
      <View style={styles.userMain}>
        <Avatar name={displayName} size={48} />
        <View style={styles.userBody}>
          <Text style={styles.name} numberOfLines={1}>{displayName}</Text>
          <Text style={styles.meta} numberOfLines={1}>Identifiant : {user.username}</Text>
          {user.email ? <Text style={styles.meta} numberOfLines={1}>{user.email}</Text> : null}
          <View style={styles.badges}>
            <Badge
              label={roleLabel(user.role)}
              tone={roleTone(user.role)}
              icon={user.role === 'ROLE_SUPER_ADMIN' ? 'shield-crown-outline' : undefined}
            />
            {user.mustChangePassword ? (
              <Badge label="Mot de passe à changer" tone="warning" icon="lock-clock" />
            ) : null}
          </View>
        </View>
      </View>
      <View style={styles.rowActions}>
        {onEditCredentials ? (
          <Button
            mode="outlined"
            icon="account-key-outline"
            textColor={colors.textPrimary}
            onPress={onEditCredentials}
          >
            Identifiants
          </Button>
        ) : null}
        {user.role === 'ROLE_AGENT' || user.role === 'ROLE_SUPER_AGENT' ? (
          <Button
            mode="outlined"
            icon="lock-reset"
            textColor={colors.textPrimary}
            onPress={onReset}
            loading={resettingId === user.id}
            disabled={resettingId !== null}
          >
            Mot de passe oublié
          </Button>
        ) : null}
      </View>
    </View>
  );
}

export default function GestionUtilisateurs() {
  const canManageAdminFeatures = useAppSelector((s) => isAdminOrSuperAgentRole(s.auth.role) || isSuperAdminRole(s.auth.role));
  const isSuperAdmin = useAppSelector((s) => isSuperAdminRole(s.auth.role));
  const currentUsername = useAppSelector((s) => s.auth.username);
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { width: windowWidth } = useWindowDimensions();
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [nom, setNom] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ROLE_AGENT' | 'ROLE_SUPER_AGENT'>('ROLE_AGENT');
  const [saving, setSaving] = useState(false);
  const [resettingId, setResettingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await userService.list());
    } catch (e: any) {
      setError(e?.response?.data?.erreur ?? 'Impossible de charger les utilisateurs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setNom('');
    setUsername('');
    setEmail('');
    setRole('ROLE_AGENT');
  };

  const handleCreate = async () => {
    const identifiant = username.trim();
    const mail = email.trim().toLowerCase();
    if (identifiant.length < 3) {
      setError("L'identifiant doit contenir au moins 3 caractères.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      setError('E-mail invalide.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await userService.createAgent({
        username: identifiant,
        email: mail,
        nom: nom.trim() || undefined,
        role,
      });
      setShowModal(false);
      resetForm();
      setNotice(`Compte créé. Un mot de passe provisoire a été envoyé à ${mail}.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.erreur ?? "Impossible de créer l'agent.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (user: AppUser) => {
    confirmAction(
      `Envoyer un nouveau mot de passe provisoire à ${user.email || user.username} ?`,
      async () => {
        setResettingId(user.id);
        setError(null);
        setNotice(null);
        try {
          await userService.resetPassword(user.id);
          setNotice(`Nouveau mot de passe provisoire envoyé à ${user.email}.`);
          await load();
        } catch (e: any) {
          setError(e?.response?.data?.erreur ?? "Impossible d'envoyer le mot de passe.");
        } finally {
          setResettingId(null);
        }
      }
    );
  };

  const handleCredentialsSaved = async (updated: AppUser) => {
    const wasSelf = editingUser?.username === currentUsername;
    setEditingUser(null);
    if (wasSelf) {
      dispatch(logout());
      return;
    }
    setError(null);
    setNotice(`Identifiants de ${updated.username} mis à jour. Ses sessions ouvertes ont été fermées.`);
    await load();
  };

  if (!canManageAdminFeatures) {
    return (
      <Screen>
        <EmptyState icon="lock-outline" message="Accès réservé à l'administrateur." />
      </Screen>
    );
  }

  return (
    <Screen>
      <PageHeader
        title="Utilisateurs"
        subtitle="Comptes agents de l'application"
        actions={(
          <Button mode="contained" icon="account-plus" onPress={() => { setError(null); setShowModal(true); }}>
            Ajouter un utilisateur
          </Button>
        )}
      />

      {notice ? <Banner tone="success" message={notice} /> : null}
      {error && !showModal ? <Banner tone="danger" message={error} /> : null}

      {loading ? (
        <ActivityIndicator color={colors.primary} style={styles.loader} />
      ) : users.length === 0 ? (
        <Card>
          <EmptyState icon="account-group-outline" message="Aucun utilisateur" />
        </Card>
      ) : (
        <>
          <SectionHeader
            title="Comptes"
            subtitle={`${users.length} utilisateur${users.length > 1 ? 's' : ''}`}
          />
          <Card flush>
            {users.map((user, index) => (
              <UserRow
                key={user.id}
                user={user}
                first={index === 0}
                resettingId={resettingId}
                onReset={() => handleReset(user)}
                onEditCredentials={isSuperAdmin ? () => { setNotice(null); setEditingUser(user); } : undefined}
              />
            ))}
          </Card>
        </>
      )}

      <Portal>
        <Modal
          visible={showModal}
          onDismiss={() => { if (!saving) { setShowModal(false); setError(null); } }}
          contentContainerStyle={[styles.modal, { width: Math.min(560, windowWidth - 32) }]}
        >
          <Text style={styles.modalTitle}>Nouvel utilisateur</Text>
          <Text style={styles.modalHint}>
            Un mot de passe provisoire sera envoyé par e-mail. L'utilisateur devra le changer à sa première connexion.
          </Text>
          {error ? <Banner tone="danger" message={error} /> : null}
          <TextInput
            label="Nom"
            value={nom}
            onChangeText={setNom}
            style={styles.input}
          />
          <TextInput
            label="Identifiant *"
            value={username}
            onChangeText={setUsername}
            autoCapitalize="none"
            style={styles.input}
          />
          <TextInput
            label="E-mail *"
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            style={styles.input}
          />
          <Text style={styles.fieldLabel}>Rôle *</Text>
          <View style={styles.roleRow}>
            {ROLE_OPTIONS.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                icon={option.value === 'ROLE_SUPER_AGENT' ? 'account-star-outline' : 'account-outline'}
                selected={role === option.value}
                onPress={() => setRole(option.value)}
              />
            ))}
          </View>
          <View style={styles.actions}>
            <Button
              textColor={colors.textPrimary}
              onPress={() => { setShowModal(false); setError(null); }}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              mode="contained"
              onPress={handleCreate}
              loading={saving}
              disabled={saving || !username.trim() || !email.trim()}
            >
              Créer et envoyer
            </Button>
          </View>
        </Modal>
      </Portal>

      <EditCredentialsModal
        user={editingUser}
        isSelf={!!editingUser && editingUser.username === currentUsername}
        onDismiss={() => setEditingUser(null)}
        onSaved={handleCredentialsSaved}
      />
    </Screen>
  );
}

const useStyles = makeStyles((c) => ({
  loader: { marginTop: 32 },
  banner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    borderRadius: RADIUS.md,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 16,
  },
  bannerText: { flex: 1, fontSize: 14, lineHeight: 20, fontWeight: '500' },

  userRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  rowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  userMain: { flex: 1, minWidth: 220, flexDirection: 'row', alignItems: 'center', gap: 14 },
  userBody: { flex: 1, minWidth: 0 },
  name: { ...TYPOGRAPHY.subheading, color: c.textPrimary },
  meta: { fontSize: 14, color: c.textSecondary, marginTop: 2 },
  badges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  rowActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' },

  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    alignSelf: 'center',
  },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary, marginBottom: 8 },
  modalHint: { fontSize: 14, lineHeight: 20, color: c.textSecondary, marginBottom: 20 },
  input: { marginBottom: 12 },
  fieldLabel: { ...TYPOGRAPHY.label, color: c.textSecondary, marginTop: 4, marginBottom: 10 },
  roleRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
}));
